import cx from "classnames";
import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { KaboodleToaster } from "components/common";
import {
  getAcceptedFileTypes,
  isValidFileExtension,
} from "components/Documents/DocumentConstants";
import type { OptionType } from "components/inputs/Select/Select";
import { useEffect, useRef, useState } from "react";
import { useConstants } from "utils/hooks";

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (
    files: File[],
    documentType?: string,
    arScreeningDocumentType?: string,
    isCmsSubmissionDocument?: boolean,
  ) => void;
  serviceType?: string;
  maxFiles?: number; // Optional property to limit number of files
}

interface DropzoneProps {
  selectedFiles?: File[];
  selectedFile?: File | null;
  onFileSelect: (files: File[] | File) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  maxFiles?: number;
  // When true, .mp4 is accepted (Organization.allow_mp4_uploads). Passed in by
  // the caller rather than read here so this component stays presentational and
  // testable without a redux store; defaults false because the current callers
  // (claim import, OCR coverage doc) are not video-upload contexts.
  allowMp4?: boolean;
}

export const Dropzone = ({
  selectedFiles = [],
  selectedFile,
  onFileSelect,
  fileInputRef,
  maxFiles = 0, // 0 means unlimited
  allowMp4 = false,
}: DropzoneProps) => {
  const acceptedFileTypes = getAcceptedFileTypes(allowMp4);

  const files =
    selectedFiles.length > 0
      ? selectedFiles
      : selectedFile
        ? [selectedFile]
        : [];

  const handleFileSelect = (newFiles: File[]) => {
    if (newFiles.length === 0) return;

    // Validate extensions on both the drag-drop and click-to-browse paths. This
    // component wires its own drop handler (not react-dropzone), so without this
    // the drag path would accept any file the OS hands over; the <input accept>
    // only filters the browse dialog.
    const validFiles = newFiles.filter((file) => {
      const isValid = isValidFileExtension(file.name, allowMp4);
      if (!isValid) {
        KaboodleToaster.error(
          `File "${file.name}" is not a supported file type.`,
        );
      }
      return isValid;
    });
    if (validFiles.length === 0) return;

    // if we're in single file mode (selectedFile prop is defined)
    if (selectedFile !== undefined) {
      onFileSelect(validFiles[0]);
      return;
    }

    // combine with existing files, respecting maxFiles limit
    let updatedFiles = [...files, ...validFiles];
    if (maxFiles > 0) {
      updatedFiles = updatedFiles.slice(0, maxFiles);
    }

    onFileSelect(updatedFiles);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length === 0) return;
    handleFileSelect(droppedFiles);
  };

  const removeFile = (index: number) => {
    if (selectedFile !== undefined) {
      onFileSelect([]);
      return;
    }

    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    onFileSelect(updatedFiles);
  };

  return (
    <figure className="kaboodle-upload-file">
      <div
        role="presentation"
        tabIndex={0}
        className="dropzone"
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={selectedFile === undefined}
          onChange={(e) =>
            e.target.files && handleFileSelect(Array.from(e.target.files))
          }
          accept={acceptedFileTypes}
          style={{ display: "none" }}
        />
        <span>
          {files.length > 0 ? (
            <div className="selected-files">
              <p>
                <b>Selected Files:</b>
              </p>
              <ul className="file-list">
                {files.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="file-item">
                    {file.name}
                    <button
                      className="remove-file"
                      onClick={(e) => {
                        e.stopPropagation(); // prevent dropzone from opening
                        removeFile(index);
                      }}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div>
              <b>Drag & Drop</b> or <b>Click</b> to Select Files
              {maxFiles > 0 && ` (Max: ${maxFiles})`}
            </div>
          )}
        </span>
      </div>
    </figure>
  );
};

const DocumentUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  // serviceType,
  maxFiles = 0, // 0 means unlimited
}: DocumentUploadModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedDocType, setSelectedDocType] = useState<OptionType | null>(
    null,
  );
  const [selectedARScreeningDocType, setSelectedARScreeningDocType] =
    useState<OptionType | null>(null);
  const [isCmsSubmissionDocument, setIsCmsSubmissionDocument] = useState(false);
  const { constants } = useConstants();

  // check if we are in the AR-Screening page
  const isARScreeningPage = window.location.pathname.includes(
    "future-medical/ar-screening/",
  );

  // get standard document types
  const documentTypes = [...(constants?.DOCUMENT_TYPES || [])].sort((a, b) =>
    (a.label || "").localeCompare(b.label || ""),
  );

  // get AR-Screening document types
  // const arScreeningDocTypes = [
  //   ...(constants?.AR_SCREENING_DOCUMENT_TYPES || []),
  // ].sort((a, b) => (a.label || "").localeCompare(b.label || ""));

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    onUpload(
      selectedFiles,
      selectedDocType?.value as string,
      selectedARScreeningDocType?.value as string,
      isCmsSubmissionDocument,
    );
    setSelectedFiles([]);
    setSelectedDocType(null);
    setSelectedARScreeningDocType(null);
    setIsCmsSubmissionDocument(false);
    onClose();
  };

  useEffect(() => {
    setSelectedDocType(documentTypes?.[0]);
  }, [documentTypes]);

  return (
    <div className={cx("overlay overlay-slidedown", { open: isOpen })}>
      <div className="grid-layout gl-1 modal-grid-wrapper">
        <div className="data-block no-hover document-upload-data">
          <div className="data-block-header flex ai-c jc-sb">
            Upload Data
            <ButtonIcon kind="neutral" onClick={onClose}>
              close
            </ButtonIcon>
          </div>

          <div>
            <label>Files:</label>
            <Dropzone
              selectedFiles={selectedFiles}
              onFileSelect={(files) =>
                setSelectedFiles(Array.isArray(files) ? files : [])
              }
              fileInputRef={fileInputRef}
              maxFiles={maxFiles}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="flex jc-c ai-c mt-1 w-100">
              <Button
                onClick={handleUpload}
                disabled={
                  isARScreeningPage
                    ? !selectedARScreeningDocType
                    : !selectedDocType
                }
              >
                Upload{" "}
                {selectedFiles.length > 1
                  ? `(${selectedFiles.length} files)`
                  : ""}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
